import { useEffect, useState } from "react";
import { Input, Modal, Select } from "antd";
import { BiChevronLeft } from "react-icons/bi";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router";
import {
  deleteCampaign,
  getAudienceCount,
  getCampaign,
  previewCampaign,
  saveCampaign,
  sendCampaign,
  sendTestCampaign,
} from "../../api/campaign";
import { showErrorToast, showSuccessToast } from "../../utils/toast";
import { usePermissionEffect } from "../../utils/usePermissionEffect";
import RichTextEditor from "./RichTextEditor";
import { AUDIENCE_LABELS, CampaignStatus } from "./shared";

const inputClass =
  "w-full text-[13px] placeholder:text-[#A0A3BD] border-[#E6E7E9] border-[1px] bg-transparent rounded-lg h-[46px] px-4";

const CampaignEditor = () => {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getModulePermission } = usePermissionEffect();

  const [form, setForm] = useState({
    subject: "",
    preheader: "",
    audience: "all",
    content: "",
  });
  const [testEmail, setTestEmail] = useState("");
  const [previewHtml, setPreviewHtml] = useState("");

  const { data: campaign } = useQuery(["email-campaign", id], () => getCampaign(id), {
    enabled: !isNew,
    // poll while a send is in progress so the counters move
    refetchInterval: (d) => (d?.status === "sending" ? 3000 : false),
    onSuccess: (c) =>
      setForm((f) =>
        f._loaded
          ? f
          : {
              subject: c.subject,
              preheader: c.preheader || "",
              audience: c.audience,
              content: c.content,
              _loaded: true,
            },
      ),
  });

  const readOnly = !isNew && campaign && campaign.status !== "draft";
  const canSend = getModulePermission("notification", "canCreate");

  const { data: audience } = useQuery(
    ["email-audience", form.audience],
    () => getAudienceCount(form.audience),
  );

  // debounced server-rendered preview, so it matches what customers get
  useEffect(() => {
    const t = setTimeout(() => {
      previewCampaign({
        subject: form.subject,
        preheader: form.preheader,
        content: form.content,
      })
        .then((r) => setPreviewHtml(r?.html || ""))
        .catch(() => {});
    }, 500);
    return () => clearTimeout(t);
  }, [form.subject, form.preheader, form.content]);

  const payload = () => ({
    id: isNew ? undefined : id,
    subject: form.subject,
    preheader: form.preheader,
    audience: form.audience,
    content: form.content,
  });

  const onSaved = (c, message) => {
    queryClient.invalidateQueries("email-campaigns");
    queryClient.invalidateQueries(["email-campaign", c?._id]);
    showSuccessToast(message);
    if (isNew && c?._id) navigate(`/email-campaigns/${c._id}`, { replace: true });
  };

  const save = useMutation(saveCampaign, {
    onSuccess: (c) => onSaved(c, "Draft saved"),
    onError: (e) => showErrorToast(e.message),
  });

  const send = useMutation(
    async () => {
      // save latest edits first, then send
      const c = await saveCampaign(payload());
      return sendCampaign(c?._id || id);
    },
    {
      onSuccess: (c) => onSaved(c, "Campaign is sending"),
      onError: (e) => showErrorToast(e.message),
    },
  );

  const test = useMutation(sendTestCampaign, {
    onSuccess: (r) => showSuccessToast(r?.message || "Test email sent"),
    onError: (e) => showErrorToast(e.message),
  });

  const remove = useMutation(() => deleteCampaign(id), {
    onSuccess: () => {
      queryClient.invalidateQueries("email-campaigns");
      showSuccessToast("Draft deleted");
      navigate("/email-campaigns");
    },
    onError: (e) => showErrorToast(e.message),
  });

  const confirmSend = () =>
    Modal.confirm({
      title: "Send this campaign?",
      content: `It will be emailed to ${Number(audience?.count || 0).toLocaleString()} ${
        AUDIENCE_LABELS[form.audience]?.toLowerCase() || "customers"
      }. This can't be undone.`,
      okText: "Send now",
      onOk: () => send.mutate(),
    });

  const busy = save.isLoading || send.isLoading;
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }));

  return (
    <div className="w-full h-full pt-5 px-5 pb-40 space-y-6 font-dmsans">
      <div className="flex items-center gap-3">
        <BiChevronLeft
          className="text-[#070E05] text-[25px] cursor-pointer"
          onClick={() => navigate("/email-campaigns")}
        />
        <div className="font-bold text-[20px] text-[#070E05]">
          {isNew ? "New email campaign" : form.subject || "Email campaign"}
        </div>
        {campaign && <CampaignStatus status={campaign.status} />}
      </div>

      {readOnly && (
        <div className="bg-white rounded-lg p-5 grid grid-cols-4 gap-4">
          {[
            ["Audience", AUDIENCE_LABELS[campaign.audience]],
            ["Delivered", Number(campaign.stats?.sent || 0).toLocaleString()],
            ["Failed", Number(campaign.stats?.failed || 0).toLocaleString()],
            [
              "Sent",
              campaign.sentAt
                ? new Date(campaign.sentAt).toLocaleString()
                : "In progress…",
            ],
          ].map(([label, value]) => (
            <div key={label}>
              <div className="text-[12px] text-[#00000080]">{label}</div>
              <div className="font-bold text-[16px]">{value}</div>
            </div>
          ))}
          {campaign.lastError && (
            <div className="col-span-4 text-[13px] text-[#EB5757]">
              Last error: {campaign.lastError}
            </div>
          )}
        </div>
      )}

      <div className="flex gap-6 items-start">
        <div className="w-1/2 bg-white rounded-lg p-5 flex flex-col gap-4">
          <div>
            <div className="text-[14px] mb-1">Subject</div>
            <input
              className={inputClass}
              placeholder="e.g. Trade gift cards at better rates this weekend"
              value={form.subject}
              onChange={set("subject")}
              maxLength={150}
              disabled={readOnly}
            />
          </div>
          <div>
            <div className="text-[14px] mb-1">
              Preview text <span className="text-[#A0A3BD]">(shown in the inbox after the subject)</span>
            </div>
            <input
              className={inputClass}
              placeholder="Optional"
              value={form.preheader}
              onChange={set("preheader")}
              maxLength={150}
              disabled={readOnly}
            />
          </div>
          <div>
            <div className="text-[14px] mb-1">Send to</div>
            <Select
              value={form.audience}
              onChange={set("audience")}
              disabled={readOnly}
              className="w-full"
              options={Object.entries(AUDIENCE_LABELS).map(([value, label]) => ({
                value,
                label,
              }))}
            />
            {!readOnly && (
              <div className="text-[12px] text-[#7B7B7B] mt-1">
                {Number(audience?.count || 0).toLocaleString()} recipients (customers who
                opted out of emails are excluded)
              </div>
            )}
          </div>
          <div>
            <div className="text-[14px] mb-1">
              Content{" "}
              <span className="text-[#A0A3BD]">
                (the Jamestown header and footer are added automatically)
              </span>
            </div>
            <RichTextEditor
              value={form.content}
              onChange={set("content")}
              disabled={readOnly}
              placeholder="Hi {{first_name}}, write your message here…"
            />
          </div>

          {!readOnly && canSend && (
            <>
              <div className="flex gap-2 items-center">
                <Input
                  placeholder="Send a test to (email)"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                />
                <button
                  type="button"
                  disabled={test.isLoading || !testEmail}
                  onClick={() =>
                    test.mutate({
                      email: testEmail,
                      subject: form.subject,
                      preheader: form.preheader,
                      content: form.content,
                    })
                  }
                  className="text-dark bg-[#0000000D] px-4 py-[6px] rounded-[5px] font-bold text-[14px] whitespace-nowrap disabled:opacity-50"
                >
                  {test.isLoading ? "Sending…" : "Send test"}
                </button>
              </div>
              <div className="flex gap-3 font-bold text-[15px]">
                <button
                  type="button"
                  disabled={busy}
                  onClick={confirmSend}
                  className="bg-dark text-white rounded-[5px] px-3 w-full py-2 disabled:opacity-50"
                >
                  {send.isLoading ? "Sending…" : "Send campaign"}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => save.mutate(payload())}
                  className="text-dark bg-[#0000000D] px-3 py-2 w-full rounded-[5px] disabled:opacity-50"
                >
                  {save.isLoading ? "Saving…" : "Save draft"}
                </button>
                {!isNew && (
                  <button
                    type="button"
                    disabled={busy || remove.isLoading}
                    onClick={() =>
                      Modal.confirm({
                        title: "Delete this draft?",
                        okText: "Delete",
                        okButtonProps: { danger: true },
                        onOk: () => remove.mutate(),
                      })
                    }
                    className="text-[#EB5757] bg-[#FF3A441A] px-3 py-2 rounded-[5px] disabled:opacity-50"
                  >
                    Delete
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        <div className="w-1/2 bg-white rounded-lg p-5 sticky top-5">
          <div className="font-bold text-[16px] mb-3">Preview</div>
          <iframe
            title="Email preview"
            srcDoc={previewHtml}
            sandbox=""
            className="w-full h-[720px] border border-[#E6E7E9] rounded-lg bg-[#f5f7fa]"
          />
        </div>
      </div>
    </div>
  );
};

export default CampaignEditor;
